import { makeAutoObservable } from 'mobx';
import { Dropzone } from './Dropzone';
import { Box } from '@apia/theme';
import { FileCard } from './FileCard';
import { ApiaUtil } from '../../objects';
import { observer } from 'mobx-react-lite';
import { uniqueId } from '@apia/util';

export type TUploadedFile = { id: string | number; name: string };

export type TUploadInProgress = {
  abortController?: AbortController;
  error?: string;
  id: string;
  file: File;
  progress: number;
};

type TUploadHandlerState = {
  inProgressFiles: TUploadInProgress[];
  uploadedFiles: TUploadedFile[];
};

export type TUploadHandlerProps = {
  initialFiles: TUploadedFile[];
  isMultiple?: boolean;
  /**
   * Must return the uploaded file instance if everything went ok.
   *
   * Must return a string if an error message must be placed in the uploading file to mark it
   * as with error.
   *
   * Must return null to remove the upload in progress without adding it to uploaded fiels.
   */
  onUpload(file: TUploadInProgress): Promise<TUploadedFile | string | null>;
  /**
   * Must return true if the file was deleted in the remote server.
   *
   * Must return a string containing an error message if something went wrong. This message
   * will be thrown as a notification.
   *
   * Must return null if nothing has to be done.
   */
  onDelete(file: TUploadedFile): Promise<true | string | null>;
};

export class UploadHandler {
  state: TUploadHandlerState = { inProgressFiles: [], uploadedFiles: [] };

  constructor(public props: TUploadHandlerProps) {
    makeAutoObservable(this);

    this.state.uploadedFiles = this.props.initialFiles;
  }

  delete(file: TUploadedFile) {
    this.props.onDelete(file).then((res) => {
      if (res === true) {
        this.state.uploadedFiles = this.state.uploadedFiles.filter(
          (c) => c !== file,
        );
      } else if (typeof res === 'string') {
        ApiaUtil.instance.notifications.notify({
          message: res,
        });
      }
    });
  }

  start(files: File[]) {
    files.forEach((file) => {
      const newUpload: TUploadInProgress = {
        abortController: new AbortController(),
        id: uniqueId('file_upload'),
        file,
        progress: 0,
      };
      this.state.inProgressFiles.push(newUpload);
      this.props.onUpload(newUpload).then((res) => {
        this.state.inProgressFiles = this.state.inProgressFiles.filter(
          (c) => c.id !== newUpload.id,
        );

        if (typeof res === 'string') {
          ApiaUtil.instance.notifications.notify({
            message: res,
            icon: '',
          });
        } else if (res) {
          this.state.uploadedFiles.push(res);
        }
      });
    });
  }
}

export const Uploader = observer(({ handler }: { handler: UploadHandler }) => {
  return (
    <Box className="file_uploader">
      <Box className="file_uploader__dropzone">
        {(handler.props.isMultiple ||
          (handler.state.uploadedFiles.length === 0 &&
            handler.state.inProgressFiles.length === 0)) && (
          <Dropzone
            onChange={(ev) => {
              handler.start(ev);
            }}
          />
        )}
      </Box>
      <Box className="file_uploader__in_progress">
        {handler.state.inProgressFiles.map((c) => (
          <Dropzone
            progress={c.progress}
            messageError={c.error}
            key={c.file.name}
          />
        ))}
      </Box>
      <Box className="file_uploader__files_list">
        {handler.state.uploadedFiles.map((c) => (
          <FileCard
            name={c.name}
            key={c.id}
            handleClose={() => handler.delete(c)}
          />
        ))}
      </Box>
    </Box>
  );
});
